import workletUrl from './levelMeter.worklet.js?url';
import { BUSES, type Bus, rampDurationMs } from './mixing';
import type { AudioTuning } from './tuning';

interface Source {
  element?: HTMLAudioElement;
  node: MediaStreamAudioSourceNode;
  meter?: AudioWorkletNode;
}

/**
 * Local playback mix. Every remote audio track is played through Web Audio into one of the buses;
 * each bus has a gain that is faded according to the mixing rules.
 *
 *   track → source → bus gain → master → speakers
 *                  ↘ level meter (whisper/announce activity)
 */
export class AudioMixer {
  readonly ctx: AudioContext;
  private master: GainNode;
  private buses: Record<Bus, GainNode>;
  private sink: GainNode;
  private sources = new Map<string, Source>();
  private ready: Promise<void>;

  /** Diagnostics: meter every source, not just whisper/announce ones. */
  meterAll = false;

  /** Level reports from metered sources, ~every 20 ms each. */
  onLevel?: (sourceId: string, levelDb: number) => void;

  constructor(private tuning: () => AudioTuning) {
    this.ctx = new AudioContext({ latencyHint: 'interactive' });
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.buses = Object.fromEntries(
      BUSES.map((b) => {
        const g = this.ctx.createGain();
        g.connect(this.master);
        return [b, g];
      }),
    ) as Record<Bus, GainNode>;
    // Meters must be connected to the destination to be processed; this keeps them silent.
    this.sink = this.ctx.createGain();
    this.sink.gain.value = 0;
    this.sink.connect(this.ctx.destination);
    this.ready = this.ctx.audioWorklet.addModule(workletUrl);
  }

  /** Must be called from a click/keypress the first time (browser autoplay rules). */
  async resume() {
    // Ask to resume synchronously, inside the click that started this (Safari requires it).
    const resumed = this.ctx.state === 'running' ? Promise.resolve() : this.ctx.resume();
    await this.ready;
    await resumed;
  }

  get running() {
    return this.ctx.state === 'running';
  }

  /** Play a remote track on a bus. */
  async play(id: string, track: MediaStreamTrack, bus: Bus, opts: { meter?: boolean } = {}) {
    await this.ready;
    this.remove(id);
    const stream = new MediaStream([track]);
    // Chrome only feeds remote WebRTC audio into Web Audio if the stream is also attached to a
    // media element. The element stays muted; the sound comes out of the graph.
    const element = new Audio();
    element.muted = true;
    element.srcObject = stream;
    element.play().catch(() => {});
    const node = this.ctx.createMediaStreamSource(stream);
    node.connect(this.buses[bus]);
    const meter = opts.meter || this.meterAll ? this.createMeter(id, node) : undefined;
    this.sources.set(id, { element, node, meter });
  }

  /** Measure a local track without playing it (your own whisper, your mic level). */
  async meterOnly(id: string, track: MediaStreamTrack) {
    await this.ready;
    this.remove(id);
    const node = this.ctx.createMediaStreamSource(new MediaStream([track]));
    this.sources.set(id, { node, meter: this.createMeter(id, node) });
  }

  remove(id: string) {
    const s = this.sources.get(id);
    if (!s) return;
    s.node.disconnect();
    if (s.meter) {
      s.meter.port.onmessage = null;
      s.meter.disconnect();
    }
    if (s.element) {
      s.element.pause();
      s.element.srcObject = null;
    }
    this.sources.delete(id);
  }

  /** Fade each bus to its target. Quieter = fade-down time, louder = fade-up time. */
  setTargets(targets: Record<Bus, number>) {
    const now = this.ctx.currentTime;
    for (const bus of BUSES) {
      const param = this.buses[bus].gain;
      const current = param.value;
      const target = targets[bus];
      const ms = rampDurationMs(current, target, this.tuning());
      param.cancelScheduledValues(now);
      param.setValueAtTime(current, now);
      if (ms === 0) param.setValueAtTime(target, now);
      else param.linearRampToValueAtTime(target, now + ms / 1000);
    }
  }

  /** Current gain of each bus (diagnostics). */
  busGains(): Record<Bus, number> {
    return Object.fromEntries(BUSES.map((b) => [b, Math.round(this.buses[b].gain.value * 100) / 100])) as Record<Bus, number>;
  }

  get supportsOutputSelection() {
    return typeof (this.ctx as AudioContext & { setSinkId?: unknown }).setSinkId === 'function';
  }

  async setOutputDevice(deviceId: string) {
    const ctx = this.ctx as AudioContext & { setSinkId?: (id: string) => Promise<void> };
    if (ctx.setSinkId) await ctx.setSinkId(deviceId === 'default' ? '' : deviceId);
  }

  async close() {
    for (const id of [...this.sources.keys()]) this.remove(id);
    await this.ctx.close().catch(() => {});
  }

  private createMeter(id: string, node: AudioNode) {
    const meter = new AudioWorkletNode(this.ctx, 'elixir-level-meter', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
    meter.port.onmessage = (e: MessageEvent<number>) => this.onLevel?.(id, e.data);
    node.connect(meter);
    meter.connect(this.sink);
    return meter;
  }
}
